import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { FormGroup, Popover, OverlayTrigger } from "react-bootstrap";
import "./ArticleComment.scss";
import "../commons.scss";
import { UiTrackedButton, TrackedFormControl } from "../common/TrackedComponents";

import type { BettyId } from "../../stores/BaseTypes";

const MAXLENGTH = 200;
const POPOVER_HEIGHT = 400;

type ArticleCommentProps = {
	comment?: string | null;
	bundleId: BettyId;
	disabled: boolean;
	setItemComment: (bundleId: string, comment: string) => void;
};

export function ArticleComment({ bundleId, comment, disabled, setItemComment }: Readonly<ArticleCommentProps>) {
	const intl = useIntl();
	const trigger = useRef<{ hide: () => void }>(null);
	const [inputValue, setInputValue] = useState(comment);
	const [commentDirty, setCommentDirty] = useState(false);
	const [placement, setPlacement] = useState("top");

	useEffect(() => {
		if (!commentDirty) {
			setInputValue(comment);
		}
	}, [comment, commentDirty]);

	const onChange = useCallback((event: React.ChangeEvent) => {
		event.preventDefault();
		setInputValue((event.target as HTMLInputElement).value || "");
		setCommentDirty(true);
	}, []);

	const onClose = useCallback(
		(event: React.SyntheticEvent) => {
			event.preventDefault();
			trigger.current?.hide();
			setInputValue(comment);
			setCommentDirty(false);
		},
		[comment],
	);

	const onDelete = useCallback(
		(event: React.SyntheticEvent) => {
			event.preventDefault();
			trigger.current?.hide();
			setInputValue("");
			setCommentDirty(false);

			if (comment) {
				setItemComment(bundleId, "");
			}
		},
		[setItemComment, bundleId, comment],
	);

	const onSave = useCallback(
		(event: React.SyntheticEvent) => {
			event.preventDefault();

			trigger.current?.hide();

			if (inputValue !== comment) {
				setItemComment(bundleId, inputValue ?? "");
			}

			setCommentDirty(false);
		},
		[inputValue, comment, setItemComment, bundleId],
	);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			switch (event.key) {
				case "Enter":
					onSave(event);
					break;
				case "Escape":
					onClose(event);
					break;
				default:
					return;
			}

			event.preventDefault();
		},
		[onSave, onClose],
	);

	const calculatePlacement = useCallback((node: HTMLInputElement) => {
		setPlacement(node.getBoundingClientRect().top < POPOVER_HEIGHT ? "bottom" : "top");
	}, []);

	const isCommentPresent = inputValue && inputValue !== "";
	return (
		<div className="article-actions-comment">
			<OverlayTrigger
				ref={trigger}
				trigger="click"
				placement={placement}
				overlay={
					<Popover id="comment-popover" className="item-comment-popover">
						<UiTrackedButton
							tags={{
								source: "ArticleComment.close",
							}}
							className="oc-articlecomment-close"
							data-testid="shopping-cart-item-comment-close"
							onClick={onClose}
						>
							<span className="icon-close" />
						</UiTrackedButton>
						<FormGroup>
							<TrackedFormControl
								tags={{
									source: "ArticleComment",
								}}
								componentClass="textarea"
								value={inputValue ?? ""}
								placeholder={intl.formatMessage({
									id: "ORC_ARTICLE_COMMENT_PLACEHOLDER",
								})}
								onChange={onChange}
								onKeyDown={handleKeyDown}
								className="item-comment-textarea"
								data-selenium="shopping-cart-item-comment-input"
								data-testid="shopping-cart-item-comment-input"
								maxLength={MAXLENGTH}
							/>
							<div className="item-comment-charcount text-right">
								<small>
									<span className="actual">{inputValue?.length ?? 0}</span>
									{"/"}
									<span className="max">{MAXLENGTH}</span>
								</small>
							</div>
						</FormGroup>
						<div className="articlecomment-buttontoolbar">
							<UiTrackedButton
								tags={{
									source: "ArticleComment.delete",
								}}
								className="item-comment-button-delete no-bottom-margin"
								data-selenium="shopping-cart-item-comment-delete"
								data-testid="shopping-cart-item-comment-delete"
								onClick={onDelete}
							>
								{intl.formatMessage({ id: "ORC_ARTICLE_COMMENT_DELETE" })}
							</UiTrackedButton>
							<UiTrackedButton
								tags={{
									source: "ArticleComment.save",
								}}
								data-variant="action"
								data-selenium="shopping-cart-item-comment-save"
								data-testid="shopping-cart-item-comment-save"
								onClick={onSave}
							>
								{intl.formatMessage({ id: "ORC_ARTICLE_COMMENT_SAVE" })}
							</UiTrackedButton>
						</div>
					</Popover>
				}
				onClick={(e) => calculatePlacement(e.target)}
			>
				<UiTrackedButton
					tags={{
						source: "ArticleComment.comment",
					}}
					id={`item-comment-${bundleId}`}
					className="item-comment btn-default btn-square"
					data-selenium="shopping-cart-item-comment"
					data-testid="shopping-cart-item-comment"
					data-comment-state={isCommentPresent ? "non-empty" : "empty"}
					disabled={disabled}
					title={inputValue}
				>
					<i className={`icon-comment${isCommentPresent ? "-filled" : ""} icon-lg`} />
				</UiTrackedButton>
			</OverlayTrigger>
		</div>
	);
}
